define('lq/conditions/conditionEvaluator',[
    'underscore',
    'when',
    'lq/events/processEventActionsUrl',
    './operandEvaluator'
], function (_, when, processEventActionsUrl, OperandEvaluator) {

    var ConditionEvaluator = function (condition) {
        if (!condition.expression) {
            piping.errorHandler.showErrorDialog(new Error('The condition "' + condition.name + '" has an empty expression.'));
            return;
        }
        this.conditionId = condition.id;
        this.conditionName = condition.name;

        this.expression = this.prepareExpression(condition.expression);
        this.name = condition.name;

        this.operands = _.map(condition.operands, function (operand) {
            return new OperandEvaluator(operand);
        });
    };

    ConditionEvaluator.prototype.evaluate = function (piping, abortSignal, runsInBackground, forceEvaluation, receivedDatasources, currentContextValue) {
        var d = when.defer();

        if (this.awaitingQueries) {
            return this.awaitingQueries;
        }

        if (this.previousExecutionData !== undefined && !forceEvaluation) {
            d.resolve(this.previousExecutionData);
        } else {
            var missingDatasources = this.findAllDataSources(receivedDatasources);

            var self = this;
            var evaluate = function (queryResults) {
                try {
                    self.previousExecutionData = self.evaluateOperands(queryResults, piping, currentContextValue);
                    delete self.awaitingQueries;
                    d.resolve(self.previousExecutionData);
                } catch (e) {
                    delete self.awaitingQueries;
                    d.reject(self.createError(e, piping));
                }
            };

            if (missingDatasources.length === 0) {
                evaluate(receivedDatasources);
            } else {
                this.awaitingQueries = d.promise;
                this.runQueries(missingDatasources, piping, abortSignal, runsInBackground)
                    .done(
                        function (queryResults) {
                            if (queryResults === 'VersionMismatch') return;
                            if (queryResults === 'abort') {
                                // note: requests are cancelled based on 2 triggers:
                                //  1 the event is aborted (abortSignal)
                                //  2 or, all requests were cancelled because a jump to was initiated
                                // we should probably unify these 2 triggers into 1 system that handles all cancellations
                                if(abortSignal.aborted) {
                                    delete self.awaitingQueries;
                                    d.resolve();
                                }
                            } else {
                                receivedDatasources = receivedDatasources || {};
                                _.forEach(queryResults, function (queryResult) {
                                    receivedDatasources[queryResult[0]] = queryResult[1];
                                });

                                evaluate(receivedDatasources);                                
                            }
                        }, function (jqXHR) {
                            delete self.awaitingQueries;
                            // the dispatcher reloads the page on a 401 (not in the background, see runtimeDispatcher.js),
                            // so there is nothing to report
                            if (jqXHR && jqXHR.status === 401 && !runsInBackground) return;
                            d.reject(self.createError(jqXHR, piping));
                        });
            }
        }

        return d.promise;
    };

    // the callers report the error: rejecting a promise they handle with done() rethrows it
    ConditionEvaluator.prototype.createError = function (e, piping) {
        // an inline condition (of a validation rule or a list) has no name or id
        var condition = this.conditionName
            ? 'the condition \'' + this.conditionName + '\' (id: ' + this.conditionId + ')'
            : 'an inline condition';
        return new Error('An error occurred while evaluating ' + condition + '.\n' + 'Cause:\n' + describeCause(e, piping));
    };

    var describeFailedRequest = function (jqXHR, piping) {
        // status 0 without an abort: the request never got an answer (network failure, timeout)
        var description = jqXHR.status === 0
            ? 'the query request got no response from the server'
            : 'the query request failed with HTTP ' + jqXHR.status + (jqXHR.statusText ? ' ' + jqXHR.statusText : '');
        // a JSON body without a message (e.g. {} on a 401) adds nothing
        var message = piping.dispatcher.getMessageFromResponse
            ? piping.dispatcher.getMessageFromResponse(jqXHR)
            : undefined;
        if (typeof message === 'string' && message)
            description += ' - ' + (message.length > 200 ? message.substring(0, 200) + '...' : message);
        return description;
    };

    var describeCause = function (e, piping) {
        if (e && e.message !== undefined) return e.message;
        // a failed request (jqXHR)
        if (e && e.status !== undefined) return describeFailedRequest(e, piping);
        return JSON.stringify(e);
    };

    ConditionEvaluator.prototype.findAllDataSources = function (receivedDatasources) {
        var dataSources = [];

        _.each(this.operands, function (operand) {
            if (OperandEvaluator.isQueryOutput(operand.leftValue) && (!receivedDatasources || !receivedDatasources[operand.leftValue.query]))
                dataSources.push(operand.leftValue.query);
            if (OperandEvaluator.isQueryOutput(operand.rightValue) && (!receivedDatasources || !receivedDatasources[operand.rightValue.query]))
                dataSources.push(operand.rightValue.query);
        });

        return dataSources;
    };

    ConditionEvaluator.prototype.runQueries = function (missingDatasources, piping, abortSignal, runsInBackground) {
        missingDatasources = _.uniq(missingDatasources);

        var dispatch = runsInBackground
            ? piping.dispatcher.backgroundDispatch
            : piping.dispatcher.dispatch;
        
        return dispatch.bind(piping.dispatcher)(processEventActionsUrl.get(piping, 'Conditions', this.name), {
                actionId: piping.actionId,
                globalParameters: piping.parameters.globals.getAll(),
                localParameters: piping.parameters.locals.getAll(),
                requests: _.map(missingDatasources, function (dataSource) {
                    return {
                        eventActionType: 'ExecuteQuery',
                        queryObjectName: dataSource,
                        queries: [
                            {
                                listeners: [],
                                params: {
                                    page: 0,
                                    pageSize: 1
                                }
                            }
                        ]
                    };
                })
            }, undefined, { signal: abortSignal })
            .then(function (results) {
                // Check if result === { status : 'VersionMismatch' }
                // The reload is handled in runtimeDispatcher.js,
                // we just need to stop the promise chain here and above in the done.
                if (results.status && results.status === 'VersionMismatch') {
                    return 'VersionMismatch';
                }
                
                return _.map(results.responses, function (response, i) {
                    var data = response.results[0].result.results.length > 0 ? response.results[0].result.results[0] : {};
                    return [missingDatasources[i], data];
                });
            })
            .catch(function(jqXHR) {
                if(jqXHR && jqXHR.statusText === 'abort') {
                    return 'abort';
                }
                // keep the promise rejected: evaluating the condition without the query's result
                // would fail with a misleading error instead of reporting the failed request
                throw jqXHR;
            });
    };

    ConditionEvaluator.prototype.evaluateOperands = function (queryResults, piping, currentContextValue) {
        var executableExpression = this.expression;
        var inputValues = {};

        _.each(this.operands, function (operand) {
            var value = operand.evaluate(queryResults, piping, currentContextValue);
            var nameToReplace = '(^| |\\()(' + operand.name + ')((?= |$|\\)))';
            inputValues[operand.name] = value;
            executableExpression = executableExpression.replace(new RegExp(nameToReplace, 'g'), '$1' + value + '$3');
        });

        return {
            condition: this,
            conditionResult: eval('(' + executableExpression + ')'),
            inputValues: inputValues,
        };
    };

    ConditionEvaluator.prototype.clearResult = function () {
        delete this.previousExecutionData;
    };

    ConditionEvaluator.prototype.prepareExpression = function (expression) {
        return expression
            .replace(/ and /gi, ' && ')
            .replace(/ or /gi, ' || ');
    };

    return ConditionEvaluator;
});
